import { useMemo } from "react"
import { dateFormat, distinct, flatJoin, flatten, numberFormat } from "shared/util"
import { useUserSession } from "features/session/context"
import { hiddenFlags } from "features/search/util"

import { SearchDetailResult } from "interface/search"
import { TargetListResult } from "interface/search/targetList"
import { LvLogResult } from "interface/carenet/lv"
import { MrpLogResult } from "interface/carenet/mrp"

import { GridColumnDefMap } from "components/grid/types"
import { formatter, columnBase, valueFormatter } from "components/grid/util"
import { renderer } from "components/grid/renderer"

import { SourceProject } from "../../types"

import { KojinResultWithEvaluationSummary } from "interface/search/evaluation"
import { EvaluationFormResult } from "interface/evaluationForm"

type useTargetGridColumnsProps = {
	search: SearchDetailResult
	evaluationForm: EvaluationFormResult | null
	categories?: TargetListResult['category']
	scores?: TargetListResult['score']
	projectList: SourceProject[]
	onClickLink: (kojinCode: string) => void
}
export const useTargetGridColumns = ({
	search,
	evaluationForm,
	categories,
	scores,
	projectList,
	onClickLink
}: useTargetGridColumnsProps) => {
	const session = useUserSession()
	const {
		hideResult,
		hideDPC,
		hideCancer,
		hideJournal,
		hideConvention,
		hideGuideLine,
		hideTrial,
		hideResearch
	} = useMemo(
		() => hiddenFlags(search),
		[search]
	)
	const columnsDef = useMemo<GridColumnDefMap<KojinResultWithEvaluationSummary>>(
		() => {
			const columnsDef: GridColumnDefMap<KojinResultWithEvaluationSummary> = {
				'link': {
					headerName: '詳細',
					sticky: true,
					hideable: false,
					value: (r) => r.kojinCode,
					...columnBase.action.openByValue(onClickLink)
				},
				'personalId': {
					headerName: 'PID',
					width: 100,
					hidden: session.isCareNet === false,
					value: (r) => r.personalId,
					filterType: 'text'
				},
				'kojinCode': {
					headerName: 'DCFコード',
					width: 80,
					value: (r) => r.kojinCode,
					filterType: 'text'
				},
				'isiName': {
					headerName: '医師名',
					width: 150,
					sticky: true,
					hideable: false,
					value: (r) => [
						r.isiNameKana ?? '',
						r.isiName ?? ''
					],
					hasKana: true,
					filterType: 'text'
				},
				'seibetu': {
					headerName: '性別',
					width: 50,
					defaultShow: false,
					value: (r) => r.seibetuCode === '1' ? '男性' : r.seibetuCode === '2' ? '女性' : '不明',
					filterType: 'candidates'
				},
				'age': {
					headerName: '年齢',
					value: (r) => r.nenrei,
					...columnBase.age
				},
				'birthDate': {
					headerName: '生年月日',
					width: 196,
					defaultShow: false,
					value: (r) => r.seinenGappi,
					valueFormatter: (value) => dateFormat(value),
					filterType: 'date'
				},
				'organ': {
					headerName: '現所属病院名',
					width: 400,
					value: (r) => r.organ?.map(organ => flatJoin('\t',
						organ.sisetuName,
						organ.bukaName,
						organ.yakusyokuName
					)),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'text'
				},
				'block': {
					headerName: 'ブロック',
					width: 100,
					defaultShow: false,
					value: (r) => distinct(r.organ?.flatMap(organ => organ.sisetuBlockName ?? []) ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'todofuken': {
					headerName: '都道府県',
					width: 100,
					defaultShow: false,
					value: (r) => distinct(r.organ?.flatMap(organ => organ.sisetuKenName ?? []) ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'bedCount': {
					headerName: '最大病床数',
					value: (r) => r.organ?.reduce<number | null>(
						(acc, organ) => organ.bedCount ? Math.max(organ.bedCount, acc ?? 0) : acc, null
					) ?? null,
					...columnBase.bedCount
				},
				'sinryoka': {
					headerName: '診療科',
					width: 200,
					value: (r) => r.sinryoka?.flatMap(sinryoka => sinryoka.sinryokaName ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'senmoni': {
					headerName: '専門医',
					width: 200,
					defaultShow: false,
					value: (r) => r.senmoni?.flatMap(senmoni => senmoni.senmoniName ? (
						senmoni.sidoui ? `${senmoni.senmoniName}指導医` :
						senmoni.senmoni ? `${senmoni.senmoniName}専門医` :
						senmoni.ninteii ? `${senmoni.senmoniName}認定医` : senmoni.senmoniName
					) : []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'gakkai': {
					headerName: '所属学会',
					width: 200,
					defaultShow: false,
					value: (r) => r.gakkai?.flatMap(gakkai => gakkai.gakkaiName ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'sotukou': {
					headerName: '出身大学',
					width: 100,
					value: (r) => r.sotukouName,
					filterType: 'candidates'
				},
				'sotuNen': {
					headerName: '卒業年',
					align: 'right',
					defaultShow: false,
					width: 80,
					valueFormatter: formatter.year,
					value: (r) => r.sotugyouYear,
					filterType: 'histgram'
				},
				'primarySinryoka': {
					headerName: 'COM診療科',
					width: 100,
					defaultShow: false,
					hidden: session.isCareNet === false,
					value: (r) => r.primarySinryoka?.sinryokaName,
					filterType: 'candidates'
				},
				/*
				'comSinryoka': {
					headerName: 'COM診療科',
					width: 200,
					defaultShow: false,
					hidden: session.isCareNet === false,
					value: (r) => r.comSinryoka?.flatMap(sinryoka => sinryoka.sinryokaName ?? []),
					renderCell: (params) => renderer.multiRow(params.value),
					filterType: 'candidates'
				},
				*/
				'kanriBunrui': {
					headerName: '勤務先分類',
					width: 100,
					defaultShow: false,
					hidden: session.isCareNet === false,
					value: (r) => (
						r.kanriBunrui === 'D01' ? 'D01:診療所 経営者' :
						r.kanriBunrui === 'D02' ? 'D02:診療所 勤務医' :
						r.kanriBunrui === 'D03' ? 'D03:小規模病院' : 
						r.kanriBunrui === 'D04' ? 'D04:中規模病院' :
						r.kanriBunrui === 'D05' ? 'D05:大病院' :
						r.kanriBunrui === 'D06' ? 'D06:大規模病院' :
						r.kanriBunrui === 'D07' ? 'D07:その他' :
						r.kanriBunrui === 'P01' ? 'P01:病院 薬剤師' :
						r.kanriBunrui === 'P02' ? 'P02:診療所 薬剤師' :
						r.kanriBunrui === 'P03' ? 'P03:調剤薬局 薬剤師' :
						r.kanriBunrui === 'P04' ? 'P04:その他 薬剤師' : r.kanriBunrui
					),
					filterType: 'candidates'
				},
				'tierRank': {
					headerName: 'Tierランク',
					width: 80,
					hidden: session.isCareNet === false,
					value: (r) => r.mrpStats?.rank?.name,
					filterType: 'candidates'
				},
				'mailStatus': {
					headerName: 'メール受信可否',
					defaultShow: false,
					hidden: session.isCareNet === false,
					value: (r) => r.mailStatus,
					filterType: 'candidates'
				},
				'cnUserType': {
					headerName: 'CN会員',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.personalId ? '会員' : '非会員',
					filterType: 'candidates'
				},
				'actorStats': {
					headerName: 'CN出演歴',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.actor ? flatten([
						r.actor.isComActor ? 'COM' : null,
						r.actor.isTvActor ? 'TV' : null,
						r.actor.isDvdActor ? 'DVD' : null
					]) : [],
					renderCell: (params) => renderer.multiRow(params.value),
					filterType: 'candidates'
				},
				'picksExpert': {
					headerName: 'DrsPicks',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.picksExpert ? 'Expert' : '-',
					filterType: 'candidates'
				},
				'topAccessTimes': {
					headerName: 'アクセス時間帯',
					width: 150,
					hidden: session.isCareNet === false,
					value: (r) => r.topAccessTimes?.map(time => {
						return (
							time.dayOfWeek === 'Mon' ? '月曜' :
							time.dayOfWeek === 'Tue' ? '火曜' :
							time.dayOfWeek === 'Wed' ? '水曜' :
							time.dayOfWeek === 'Thu' ? '木曜' :
							time.dayOfWeek === 'Fri' ? '金曜' :
							time.dayOfWeek === 'Sat' ? '土曜' :
							time.dayOfWeek === 'Sun' ? '日曜' : ''
						) + `\t${time.hourOfDay * 3}～${time.hourOfDay * 3 + 2}時`
					}),
					renderCell: (params) => renderer.multiRow(params.value),
					filterType: 'accessTime',
					split: 3
				},
				'academiaSinryoka': {
					headerName: 'AC 診療科',
					defaultShow: false,
					width: 100,
					hidden: session.isCareNet === false,
					value: (r) => r.academia?.sinryoka?.name,
					filterType: 'candidates'
				},
				'academiaDiseases': {
					headerName: 'AC 選択疾患',
					defaultShow: false,
					width: 150,
					hidden: session.isCareNet === false,
					value: (r) => r.academia?.diseases?.map(disease => disease.name),
					renderCell: (params) => renderer.multiRow(params.value, "horizon", 10),
					filterType: 'candidates'
				},
				'academiaLastPageView': {
					headerName: 'AC 前月PV',
					defaultShow: false,
					width: 100,
					hidden: session.isCareNet === false,
					type: 'number',
					align: 'right',
					value: (r) => r.academia?.pageView,
					filterType: 'histgram'
				},
				'academiaLastInterest': {
					headerName: 'AC 前月Interest',
					defaultShow: false,
					width: 100,
					hidden: session.isCareNet === false,
					type: 'number',
					align: 'right',
					value: (r) => r.academia?.interestCount,
					filterType: 'histgram'
				},
				'rank': {
					headerName: '総合順位',
					hidden: hideResult,
					value: (r) => r.score?.rank,
					...columnBase.rank
				},
				'dpcScore': {
					headerName: 'DPCスコア',
					hidden: hideResult || hideDPC,
					value: (r) => r.score?.dpc?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { dpcRank?: number }) => {
						return (value && value !== Infinity && row.dpcRank && row.dpcRank !== Infinity) ? (
							`${numberFormat(value)}件 (${row.dpcRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'dpcRank',
					filterType: 'histgram'
				},
				'dpcRank': {
					headerName: 'DPC順位',
					hidden: true,
					value: (r) => r.score?.dpc?.rank,
					...columnBase.rank
				},
				'cancerScore': {
					headerName: 'がんスコア',
					hidden: hideResult || hideCancer,
					defaultShow: false,
					value: (r) => r.score?.cancer?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { cancerRank?: number }) => {
						return (value && value !== Infinity && row.cancerRank && row.cancerRank !== Infinity) ? (
							`${numberFormat(Math.floor(value * 100) / 100)}pt (${row.cancerRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'cancerRank',
					filterType: 'histgram'
				},
				'cancerRank': {
					headerName: 'がん順位',
					hidden: true,
					value: (r) => r.score?.cancer?.rank,
					...columnBase.rank
				},
				'trialScore': {
					headerName: '治験・研究スコア',
					hidden: hideResult || hideTrial,
					defaultShow: false,
					value: (r) => r.score?.trial?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { trialRank?: number }) => {
						return (value && value !== Infinity && row.trialRank && row.trialRank !== Infinity) ? (
							`${numberFormat(Math.floor(value * 100) / 100)}pt (${row.trialRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'trialRank',
					filterType: 'histgram'
				},
				'trialRank': {
					headerName: '治験・研究順位',
					hidden: true,
					value: (r) => r.score?.trial?.rank,
					...columnBase.rank
				},
				'trialRoles': {
					headerName: '治験での役割',
					hidden: hideResult || hideTrial,
					width: 120,
					defaultShow: false,
					value: (r) => r.score?.trial?.roles,
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'trialCount': {
					headerName: '治験参加数',
					hidden: hideResult || hideTrial,
					value: (r) => r.score?.trial?.trialCount,
					...columnBase.count
				},
				'guidelineScore': {
					headerName: 'ガイドラインスコア',
					hidden: hideResult || hideGuideLine,
					defaultShow: false,
					value: (r) => r.score?.guideline?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { guidelineRank?: number }) => {
						return (value && value !== Infinity && row.guidelineRank && row.guidelineRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.guidelineRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'guidelineRank',
					filterType: 'histgram'
				},
				'guidelineRank': {
					headerName: 'ガイドライン順位',
					hidden: true,
					value: (r) => r.score?.guideline?.rank,
					...columnBase.rank
				},
				'journalScore': {
					headerName: '論文スコア',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { journalRank?: number, journalScore: number }) => {
						return (value && value !== Infinity && row.journalRank && row.journalRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.journalRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'journalRank',
					filterType: 'histgram'
				},
				'journalRank': {
					headerName: '論文順位',
					hidden: true,
					value: (r) => r.score?.journal?.rank,
					...columnBase.rank
				},
				'single': {
					headerName: '単独論文',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.single,
					...columnBase.count
				},
				'first': {
					headerName: '筆頭著者',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.first,
					...columnBase.count
				},
				'second': {
					headerName: '第二著者',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.second,
					...columnBase.count
				},
				'last': {
					headerName: '責任著者',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.last,
					...columnBase.count
				},
				'lastSecond': {
					headerName: '第二責任著者',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.lastSecond,
					...columnBase.count
				},
				'total': {
					headerName: '合計論文数',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.total,
					...columnBase.count
				},
				'researchScore': {
					headerName: '研究スコア',
					hidden: hideResult || hideResearch,
					defaultShow: false,
					value: (r) => r.score?.research?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { researchRank?: number }) => {
						return (value && value !== Infinity && row.researchRank && row.researchRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.researchRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'researchRank',
					filterType: 'histgram'
				},
				'researchRank': {
					headerName: '研究順位',
					hidden: true,
					value: (r) => r.score?.research?.rank,
					...columnBase.rank
				},
				'convention': {
					headerName: '学会',
					width: 300,
					hidden: hideResult || hideConvention,
					value: (r) => r.convention?.flatMap(
						cnv => cnv.score > 0 ? flatJoin('\t',
							cnv.numberOfTimes,
							cnv.conventionName?.replaceAll('　', ' '),
							cnv.role
						) : []
					),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				}
			}

			if (hideResult === false && hideGuideLine === false) {
				const DEF = '__default_'
				const guideLineTypes = distinct(search.guideLine?.flatMap(
					guideLine => guideLine.hasDoctor ? (guideLine.guideLineType ?? DEF) : []
				) ?? [])
				guideLineTypes.sort((a, b) => a === DEF ? -1 : b === DEF ? 1 : a.localeCompare(b))
				guideLineTypes.forEach((guideLineType) => {
					const field = guideLineType === DEF ? 'guideLineKojin' : `guideLineKojin-${guideLineType}`
					columnsDef[field] = {
						headerName: guideLineType === DEF ? 'ガイドライン' : guideLineType,
						width: 300,
						value: (r) => r.guideLine?.flatMap(
							guide => {
								const type = search.guideLine?.find(
									guideLine => guideLine.guideLineId === guide.guideLineId
								)?.guideLineType ?? null
								if (guideLineType === DEF) {
									if (type !== null) return []
								} else {
									if (type !== guideLineType) return []
								}
								if (guide.score === 0) return []
								return flatJoin('\t',
									guide.guideLineName?.replaceAll('　', ' '),
									guide.researcherClass,
									guide.postNames
								)
							}
						),
						renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
						filterType: "text"
					}
				})
			}
			if (hideResult === false && search.metaTag) {
				search.metaTag.forEach(metaTag => {
					columnsDef[`meta-${metaTag.key}`] = {
						headerName: metaTag.label,
						width: 200,
						value: (r) => r.metaTag?.flatMap(kojinsMetaTag => kojinsMetaTag.key === metaTag.key ? kojinsMetaTag.value : []),
						renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
						filterType: 'candidates'
					}
				})
			}
			if (evaluationForm) {
				if (evaluationForm.sections) {
					columnsDef['evaluation-total'] = {
						headerName: '総合評価',
						width: 80,
						align: 'right',
						value: ({ evaluation }) => evaluation?.totalScore,
						valueFormatter: (value) => valueFormatter({ value, unit: '点' }),
						suffix: '点',
						filterType: 'histgram'
					}
					columnsDef['evaluation-judgement'] = {
						headerName: '総合判定',
						width: 150,
						value: ({ evaluation }) => evaluation?.judgement,
						filterType: 'candidates'
					}
					evaluationForm.sections.forEach((section, i) => {
						columnsDef[`evaluation-section-${i}-total`] = {
							headerName: section.sectionName,
							width: 80,
							align: 'right',
							defaultShow: false,
							value: ({ evaluation }) => evaluation?.sections?.at(i)?.sectionTotal,
							valueFormatter: (value) => valueFormatter({ value, unit: '点' }),
							suffix: '点',
							filterType: 'histgram'
						}
						section.fields.forEach((field, j) => {
							columnsDef[`evaluation-section-${i}-${j}-content`] = {
								headerName: field.contentFieldId,
								width: 200,
								defaultShow: false,
								value: ({ evaluation }) => evaluation?.sections?.at(i)?.records.at(j)?.content,
								filterType: 'text'
							}
							columnsDef[`evaluation-section-${i}-${j}-score`] = {
								headerName: field.scoreFieldId,
								width: 60,
								defaultShow: false,
								align: 'center',
								value: ({ evaluation }) => evaluation?.sections?.at(i)?.records.at(j)?.score,
								filterType: 'candidates'
							}
						})
					})
					evaluationForm.considerations.forEach((consideration, i) => {
						columnsDef[`evaluation-consideration-${i}`] = {
							headerName: consideration,
							width: 200,
							defaultShow: false,
							value: ({ evaluation }) => evaluation?.considerations?.at(i)?.content,
							filterType: 'text'
						}
					})
				}
			}
			categories?.forEach((category, i) => {
				columnsDef[`category-${i}`] = {
					headerName: category.title,
					width: 100,
					value: (r) => {
						if (!r.target?.category) return null
						return r.target.category.find(({ sequence }) => i === sequence)?.name
					},
					filterType: 'candidates'
				}
			})
			scores?.forEach((score, i) => {
				columnsDef[`score-${i}`] = {
					headerName: score.title,
					width: 100,
					align: 'right',
					value: (r) => {
						if (!r.target?.score) return null
						return r.target.score.find(({ sequence }) => i === sequence)?.score
					},
					filterType: 'histgram'
				}
			})
			projectList.forEach((project, i) => {
				if (project.source === 'lv') {
					columnsDef[`project-lv-${i}`] = {
						headerName: `${i+1}:視聴状態`,
						width: 100,
						value: (r) => {
							if (!r.personalId) return null
							const log = project.logList.find(log => log.personalId === r.personalId)
							if (!log) return '未視聴'
							const lvLog = log as LvLogResult
							if (lvLog.broadcastDate) {
								if (lvLog.bonusEnqueteDate || lvLog.clientEnqueteDate) return '視聴+EQ'
								return '視聴'
							} else if (lvLog.ondemandDate) {
								if (lvLog.bonusEnqueteDate || lvLog.clientEnqueteDate) return 'OD視聴+EQ'
								return 'OD視聴'
							} else {
								if (lvLog.bonusEnqueteDate || lvLog.clientEnqueteDate) return 'EQのみ'
								if (lvLog.reserveDate) return '予約'
								return '未視聴'
							}
						},
						filterType: 'candidates'
					}
					columnsDef[`project-lv-duration-${i}`] = {
						headerName: `${i+1}:視聴時間`,
						width: 100,
						align: 'right',
						value: (r) => {
							if (!r.personalId) return null
							const log = project.logList.find(log => log.personalId === r.personalId)
							if (!log) return null
							const lvLog = log as LvLogResult
							return lvLog.duration
						},
						valueFormatter: formatter.second,
						suffix: '秒',
						filterType: 'histgram'
					}
				} else if (project.source === 'mrp') {
					columnsDef[`project-mrp-${i}`] = {
						headerName: `${i+1}:視聴状態`,
						width: 100,
						value: (r) => {
							if (!r.personalId) return null
							const log = project.logList.find(log => log.personalId === r.personalId)
							if (!log) return '未視聴'
							const mrpLog = log as MrpLogResult
							if (mrpLog.firstviewdate) return '視聴済'
							return '未視聴'
						},
						filterType: 'candidates'
					}
				}
			})
			return columnsDef
		},
		[
			categories, scores, search, session, onClickLink, evaluationForm, projectList,
			hideResult, hideDPC, hideCancer, hideJournal, hideConvention, hideGuideLine, hideTrial, hideResearch
		]
	)
	return { columnsDef }
}